import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Mail,
  DollarSign,
  CheckCircle,
  Clock,
  LogOut,
  User,
  Plus,
  Trash2,
  MousePointerClick,
  Camera,
  Share2,
  ArrowRight,
  AlertCircle,
  Wallet,
  FileText,
  MapPin,
  HelpCircle,
  Globe
} from "lucide-react";
import OnboardingFlow from "../components/OnboardingFlow";

export default function WorkerDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [registeredEmails, setRegisteredEmails] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [withdrawalMethod, setWithdrawalMethod] = useState("paypal");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, submissionsRes, emailsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/v2/tasks?is_active=true`, { headers }),
        axios.get(`${API}/v2/submissions`, { headers }),
        axios.get(`${API}/v2/users/emails`, { headers }),
        axios.get(`${API}/v2/settings`, { headers })
      ]);
      // Handle paginated responses - extract items array
      setTasks(tasksRes.data.items || tasksRes.data);
      setSubmissions(submissionsRes.data.items || submissionsRes.data);
      setRegisteredEmails(emailsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail) {
      toast.error("Please enter an email address");
      return;
    }
    
    // Validate Gmail only
    const emailLower = newEmail.toLowerCase().trim();
    if (!emailLower.endsWith('@gmail.com')) {
      toast.error("Only Gmail addresses (@gmail.com) are accepted");
      return;
    }
    
    // Check max limit
    if (registeredEmails.length >= 10) {
      toast.error("Maximum 10 email addresses allowed");
      return;
    }
    
    // Check for duplicates
    if (registeredEmails.some(e => e.email.toLowerCase() === emailLower)) {
      toast.error("This email is already registered");
      return;
    }
    
    try {
      await axios.post(`${API}/v2/users/emails`, { email: emailLower }, { headers });
      toast.success("Gmail added successfully! Check your inbox for verification.");
      setNewEmail("");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add email");
    }
  };

  const handleRemoveEmail = async (emailId) => {
    try {
      await axios.delete(`${API}/v2/users/emails/${emailId}`, { headers });
      toast.success("Email removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove email");
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawalAmount);
    if (!amount || amount < (settings?.min_withdrawal || 10)) {
      toast.error(`Minimum withdrawal is $${settings?.min_withdrawal || 10}`);
      return;
    }

    const pointsNeeded = amount * (settings?.points_per_usd || 100);
    if (pointsNeeded > user.points_balance) {
      toast.error("Insufficient points balance");
      return;
    }

    try {
      await axios.post(`${API}/v2/withdrawals`, {
        amount,
        payment_method: withdrawalMethod,
        paypal_email: withdrawalMethod === "paypal" ? paypalEmail : null
      }, { headers });
      toast.success("Withdrawal request submitted");
      setWithdrawDialogOpen(false);
      setWithdrawalAmount("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit withdrawal");
    }
  };

  const handleResendVerification = async (emailId) => {
    try {
      await axios.post(`${API}/v2/users/emails/${emailId}/resend-verification`, {}, { headers });
      toast.success("Verification email sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend verification");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getTaskIcon = (type) => {
    switch (type) {
      case "click_url": return MousePointerClick;
      case "reply_email": return Mail;
      case "social_post": return Share2;
      case "social_review": return Camera;
      default: return CheckCircle;
    }
  };

  const getTaskColor = (type) => {
    switch (type) {
      case "click_url": return "bg-blue-500";
      case "reply_email": return "bg-green-500";
      case "social_post": return "bg-purple-500";
      case "social_review": return "bg-orange-500";
      default: return "bg-slate-500";
    }
  };

  const isTaskCompleted = (taskId) => {
    return submissions.some(s => s.task_id === taskId && s.status !== "rejected");
  };

  const pendingSubmissions = submissions.filter(s => s.status === "pending").length;
  const approvedSubmissions = submissions.filter(s => s.status === "approved").length;
  const totalEarned = submissions.filter(s => s.status === "approved").reduce((acc, s) => acc + (s.points_awarded || 0), 0);
  const usdBalance = settings ? (user?.points_balance || 0) / settings.points_per_usd : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="worker-dashboard">
      {/* Onboarding Flow - Shows dialogs for terms and additional info */}
      <OnboardingFlow onComplete={() => setOnboardingComplete(true)} />
      
      {/* Header - Mobile optimized */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/images/gigseasy-logo.svg" 
                alt="GigsEasy Logo" 
                className="h-8 sm:h-10"
              />
              <div>
                <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Gigster Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              {user?.status === "pending" && (
                <Badge 
                  variant="outline" 
                  className="bg-amber-50 text-amber-700 border-amber-200 text-xs sm:text-sm px-2 py-1 cursor-pointer hover:bg-amber-100"
                  onClick={() => window.location.reload()}
                  title="Click to start setup"
                >
                  <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  <span className="hidden sm:inline">
                    {user?.onboarding_status === "info_completed" 
                      ? "Pending Admin Approval" 
                      : "Complete Setup →"}
                  </span>
                  <span className="sm:hidden">Pending</span>
                </Badge>
              )}
              <Link to="/support">
                <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10" data-testid="support-btn" title="Support">
                  <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </Link>
              <Link to="/profile">
                <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10" data-testid="profile-btn">
                  <User className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </Link>
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Stats Grid - Mobile optimized */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-8">
          <Card className="border-slate-200">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-amber-100 flex items-center justify-center rounded-lg">
                  <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-amber-600" />
                </div>
                <Badge variant="outline" className="text-amber-600 border-amber-200 text-xs hidden sm:inline-flex">Points</Badge>
              </div>
              <p className="text-xl sm:text-3xl font-heading font-bold text-slate-900">{(user?.points_balance || user?.points || 0).toLocaleString()}</p>
              <p className="text-xs sm:text-sm text-slate-500">≈ ${usdBalance.toFixed(2)}</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 flex items-center justify-center rounded-lg">
                  <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
                </div>
                <Badge variant="outline" className="text-green-600 border-green-200 text-xs hidden sm:inline-flex">Done</Badge>
              </div>
              <p className="text-xl sm:text-3xl font-heading font-bold text-slate-900">{approvedSubmissions}</p>
              <p className="text-xs sm:text-sm text-slate-500">Completed</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-100 flex items-center justify-center rounded-lg">
                  <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <Badge variant="outline" className="text-blue-600 border-blue-200 text-xs hidden sm:inline-flex">Pending</Badge>
              </div>
              <p className="text-xl sm:text-3xl font-heading font-bold text-slate-900">{pendingSubmissions}</p>
              <p className="text-xs sm:text-sm text-slate-500">In review</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-purple-100 flex items-center justify-center rounded-lg">
                  <Mail className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
                </div>
                <Badge variant="outline" className="text-purple-600 border-purple-200 text-xs hidden sm:inline-flex">Emails</Badge>
              </div>
              <p className="text-xl sm:text-3xl font-heading font-bold text-slate-900">{registeredEmails.length}</p>
              <p className="text-xs sm:text-sm text-slate-500">Registered</p>
            </CardContent>
          </Card>
        </div>

        {/* Withdrawal Section - Mobile optimized */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-8">
          <Card className="lg:col-span-2 border-slate-200">
            <CardHeader className="border-b border-slate-100 p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg sm:text-xl">Registered Emails</CardTitle>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">{registeredEmails.length}/10 Gmail addresses</p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="h-9 text-sm" data-testid="add-email-btn" disabled={registeredEmails.length >= 10}>
                      <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Add Email</span>
                      <span className="sm:hidden">Add</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="mx-4 sm:mx-auto max-w-[calc(100%-2rem)] sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-primary" />
                        Add Gmail Address
                      </DialogTitle>
                      <DialogDescription className="text-left">
                        Register your Gmail addresses to receive and complete tasks.
                      </DialogDescription>
                    </DialogHeader>
                    
                    {/* Info Banner */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 font-bold text-sm">10x</span>
                        </div>
                        <div>
                          <p className="font-medium text-blue-900 text-sm">Maximize your earnings!</p>
                          <p className="text-xs sm:text-sm text-blue-700 mt-1">
                            You can register up to <strong>10 Gmail addresses</strong>. More emails means more task opportunities and up to <strong>10x more earnings</strong> compared to just one email.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm">Gmail Address</Label>
                        <div className="relative mt-1.5">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="yourname@gmail.com"
                            className="h-11 pl-10"
                            data-testid="new-email-input"
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Only Gmail addresses (@gmail.com) are accepted
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                        <span>Registered emails:</span>
                        <span className="font-medium">{registeredEmails.length} / 10</span>
                      </div>

                      <Button onClick={handleAddEmail} className="w-full h-11" data-testid="save-email-btn">
                        Add Gmail & Send Verification
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {registeredEmails.length === 0 ? (
                <div className="p-6 sm:p-8 text-center">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-7 h-7 sm:w-8 sm:h-8 text-blue-500" />
                  </div>
                  <p className="text-base sm:text-lg font-medium text-slate-900 mb-1">No Gmail addresses registered</p>
                  <p className="text-sm text-slate-500 mb-4">Add up to 10 Gmail addresses to maximize your earnings</p>
                  <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs sm:text-sm text-amber-700">
                    <AlertCircle className="w-4 h-4" />
                    More emails = More tasks = More money!
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {registeredEmails.map((email) => (
                    <div key={email.email_id} className="p-3 sm:p-4 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 ${email.is_verified ? 'bg-green-100' : 'bg-amber-100'} flex items-center justify-center rounded-lg`}>
                          {email.is_verified ? (
                            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                          ) : (
                            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-slate-900 text-sm sm:text-base truncate">{email.email}</p>
                            {email.is_verified ? (
                              <Badge className="bg-green-100 text-green-700 text-xs">Verified</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Pending</Badge>
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-slate-500">Added {new Date(email.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        {!email.is_verified && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleResendVerification(email.email_id)}
                            className="text-primary h-8 px-2 sm:px-3 text-xs sm:text-sm"
                            data-testid={`resend-verification-${email.email_id}`}
                          >
                            Resend
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleRemoveEmail(email.email_id)}
                          data-testid={`remove-email-${email.email_id}`}
                        >
                          <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 p-4 sm:p-6">
              <CardTitle className="font-heading text-lg sm:text-xl">Withdraw Funds</CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="mb-4 sm:mb-6">
                <p className="text-xs sm:text-sm text-slate-500 mb-1">Available Balance</p>
                <p className="text-2xl sm:text-3xl font-heading font-bold text-slate-900">${usdBalance.toFixed(2)}</p>
                <p className="text-xs sm:text-sm text-slate-500">{(user?.points_balance || user?.points || 0).toLocaleString()} points</p>
              </div>
              <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-slate-600 mb-4 sm:mb-6">
                <p>Min withdrawal: ${settings?.min_withdrawal || 10}</p>
                <p className="text-amber-600 font-medium">Transaction Fee: 5%</p>
                <p className="flex items-center gap-1 text-slate-500">
                  <Globe className="w-3 h-3" />
                  Payment methods: PayPal, Custom
                </p>
                <p className="text-xs text-slate-400 italic">Availability varies based on your country</p>
              </div>
              <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="w-full h-11" 
                    disabled={usdBalance < (settings?.min_withdrawal || 10) || user?.status !== "active"}
                    data-testid="withdraw-btn"
                  >
                    <Wallet className="w-4 h-4 mr-2" />
                    Request Withdrawal
                  </Button>
                </DialogTrigger>
                <DialogContent className="mx-4 sm:mx-auto max-w-[calc(100%-2rem)] sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Request Withdrawal</DialogTitle>
                    <DialogDescription>
                      Choose your payment method and amount
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Amount (USD)</Label>
                      <Input
                        type="number"
                        value={withdrawalAmount}
                        onChange={(e) => setWithdrawalAmount(e.target.value)}
                        placeholder={`Min $${settings?.min_withdrawal || 10}`}
                        className="h-11"
                        data-testid="withdrawal-amount-input"
                      />
                    </div>
                    <div>
                      <Label>Payment Method</Label>
                      <Select value={withdrawalMethod} onValueChange={setWithdrawalMethod}>
                        <SelectTrigger className="h-11" data-testid="withdrawal-method-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paypal">PayPal</SelectItem>
                          <SelectItem value="custom">Custom (Contact Admin)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        Payment method availability varies based on your country
                      </p>
                    </div>
                    {withdrawalMethod === "paypal" && (
                      <div>
                        <Label>PayPal Email</Label>
                        <Input
                          type="email"
                          value={paypalEmail}
                          onChange={(e) => setPaypalEmail(e.target.value)}
                          placeholder="your@paypal.com"
                          className="h-11"
                          data-testid="paypal-email-input"
                        />
                      </div>
                    )}
                    
                    {/* Fee Breakdown Preview */}
                    {withdrawalAmount && parseFloat(withdrawalAmount) > 0 && (
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-testid="withdrawal-fee-breakdown">
                        <h4 className="text-sm font-medium text-slate-700 mb-3">Withdrawal Summary</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Requested Amount:</span>
                            <span className="font-medium">${parseFloat(withdrawalAmount).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-amber-600">
                            <span>Transaction Fee (5%):</span>
                            <span className="font-medium">-${(parseFloat(withdrawalAmount) * 0.05).toFixed(2)}</span>
                          </div>
                          <div className="border-t border-slate-200 pt-2 mt-2">
                            <div className="flex justify-between">
                              <span className="text-slate-700 font-medium">You Will Receive:</span>
                              <span className="font-bold text-green-600">${(parseFloat(withdrawalAmount) * 0.95).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <Button onClick={handleWithdraw} className="w-full h-11" data-testid="submit-withdrawal-btn">
                      Submit Request
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>

        {/* Available Tasks - Mobile optimized */}
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 p-4 sm:p-6">
            <CardTitle className="font-heading text-lg sm:text-xl">Available Tasks</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {user?.status !== "active" ? (
              <div className="p-6 sm:p-8 text-center text-slate-500">
                <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-amber-400" />
                {user?.onboarding_status === "info_completed" ? (
                  <>
                    <p className="font-medium text-base sm:text-lg text-slate-700">Account Pending Admin Approval</p>
                    <p className="text-xs sm:text-sm mt-2">Your registration is complete. Please wait for admin to review and approve your account.</p>
                    <p className="text-xs sm:text-sm text-slate-400 mt-1">You will be notified once approved.</p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-lg text-slate-700">Complete Your Setup</p>
                    <p className="text-sm mt-2 mb-4">Please complete all onboarding steps to access tasks</p>
                    <Button 
                      onClick={() => window.location.reload()} 
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="start-onboarding-btn"
                    >
                      Start Setup Process
                    </Button>
                    <p className="text-xs text-slate-400 mt-3">
                      Steps: Accept Terms → Provide Contact Info → Wait for Approval
                    </p>
                  </>
                )}
              </div>
            ) : tasks.length === 0 ? (
              <div className="p-6 sm:p-8 text-center text-slate-500">
                <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-slate-300" />
                <p className="text-sm sm:text-base">No tasks available at the moment</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {tasks.map((task) => {
                  const TaskIcon = getTaskIcon(task.task_type);
                  const completed = isTaskCompleted(task.task_id);
                  return (
                    <div key={task.task_id} className="p-3 sm:p-4 hover:bg-slate-50 transition-colors">
                      {/* Mobile: Stack layout, Desktop: Row layout */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 ${getTaskColor(task.task_type)} flex items-center justify-center flex-shrink-0 rounded-lg`}>
                            <TaskIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5 sm:mb-1 flex-wrap">
                              <h3 className="font-medium text-slate-900 text-sm sm:text-base truncate">{task.title}</h3>
                              {completed && (
                                <Badge className="bg-green-100 text-green-700 text-xs">Done</Badge>
                              )}
                            </div>
                            <p className="text-xs sm:text-sm text-slate-500 line-clamp-2 sm:truncate">{task.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 pl-13 sm:pl-0">
                          <div className="text-left sm:text-right">
                            <p className="font-heading font-bold text-amber-600 text-sm sm:text-base">{task.points_reward} pts</p>
                            <p className="text-xs text-slate-400">
                              {task.completion_method === "auto" ? "Auto" : "Review"}
                            </p>
                          </div>
                          {!completed && (
                            <Link to={`/task/${task.task_id}`}>
                              <Button size="sm" className="h-9 px-4" data-testid={`start-task-${task.task_id}`}>
                                Start
                                <ArrowRight className="w-4 h-4 ml-1" />
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
